import {
  NS
} from '@/constants';
import { isUserEmpty } from '@/utils/comparisonUtils';
import { getI18NextConfigForLocale } from '@/utils/locales';
import { isFeatureEnabled, sanitizeObject } from '@/utils/millesimaUtils';
import { prefetchLayout } from '@/utils/prefetchLayout';
import {
  getAttribute,
  getAttributeValue,
} from '@/utils/productUtils';
import { ATTRIBUTES } from '@/constants/product';
import { CHART_COLORS, STORAGE_SUBMENU_LINKS, STORAGE_TABS } from '@/constants/storage';
import { COLOR_LABEL } from '@/constants/styles';
import { FEATURES } from '@/constants/features';
import { FOOTER_TYPE } from '@/constants/footer';
import { HEADER_TYPE } from '@/constants/header';
import { PAGES } from '@/constants/pages';

const NAMESPACES = [NS.COMMON, NS.CELLAR, NS.CATEG, NS.ALTER, NS.CHECKOUT];

const mapViewName = (link) => {
  const mapping = {
    [STORAGE_TABS.MY_CELLAR]: 'MyCellarView',
    [STORAGE_TABS.HISTORY]: 'HistoryView',
    [STORAGE_TABS.WINEBOX]: 'WineBoxView',
    [STORAGE_TABS.SIGN_CONTRACT]: 'NoContractView'
  };

  return mapping[link] || 'DashboardView';
};

const getViewProps = async (ctx, store, link) => {
  const componentName = mapViewName(link);

  const loadedModule = componentName ? await import('@/components/views/cellar/' + componentName + '.tsx') : null;

  if (loadedModule?.prefetchView) {
    await loadedModule.prefetchView(ctx, store);
  }
};

export const getCellarServerSideProps = async (ctx, store, serverSideTranslations) => {
  const layoutOptions = await prefetchLayout(ctx, store);
  const { admin, auth, root, user } = store.getState();
  const { locale, features, url } = root.storeConf || ctx;
  const { query } = ctx;
  const link = query?.link;
  const { storageService } = store.services;

  if (link && !Object.values(STORAGE_TABS).includes(link)) {
    return {
      redirect: {
        destination: PAGES.CELLAR,
        permanent: false
      }
    };
  }

  if (isUserEmpty(auth.loggedUser)) {
    return {
      redirect: {
        destination: `${PAGES.AUTH}?origin=${encodeURIComponent(PAGES.ACCOUNT)}`,
        permanent: false
      }
    };
  } else if ((!isUserEmpty(auth.loggedUser) && !user?.content?.hasStorage) ||
    (!isFeatureEnabled({ features }, FEATURES.MODULE_MY_CELLAR) && !admin.actAsCustomer)) {
    // if user no stockage --> redirect HOME
    // TO DO redirect on information page cellar in my account ?
    return {
      redirect: {
        destination: url,
        permanent: false
      }
    };
  }

  const signatureInfo = await storageService.checkSignStatus();
  if (link !== STORAGE_TABS.SIGN_CONTRACT && signatureInfo && !admin.actAsCustomer) {
    return {
      redirect: {
        destination: `${PAGES.CELLAR}/${STORAGE_TABS.SIGN_CONTRACT}`,
        permanent: false
      }
    };
  } else if (link === STORAGE_TABS.SIGN_CONTRACT && !signatureInfo) {
    return {
      redirect: {
        destination: PAGES.CELLAR,
        permanent: false
      }
    };
  }

  await getViewProps(ctx, store, link);

  const translate = await serverSideTranslations(locale, NAMESPACES, getI18NextConfigForLocale(locale));

  return {
    props: sanitizeObject({
      ...translate,
      link,
      signatureInfo,
      withCellarLayout: link !== STORAGE_TABS.SIGN_CONTRACT,
      layoutOptions: {
        ...layoutOptions,
        withPromotionHeader: false,
        footerType: FOOTER_TYPE.CELLAR,
        headerType: HEADER_TYPE.CELLAR,
        withNavBar: false,
        withWineBoxModal: false
      },
      initialReduxState: store.getState()
    })
  };
};

export const formatStorageMenu = (t) => {
  return STORAGE_SUBMENU_LINKS.map((link) => (
    { title: t(link.title), url: link.url }
  ));
};

export const getItemAttributes = (storageItem) => {
  const attributes = [];
  const region = getAttribute(storageItem, ATTRIBUTES.REGION);
  const appellation = getAttribute(storageItem, ATTRIBUTES.APPELLATION);

  if (region.value) {
    attributes.push({ key: ATTRIBUTES.REGION, attribute: region });
  }
  if (appellation.value) {
    attributes.push({ key: ATTRIBUTES.APPELLATION, attribute: appellation });
  }

  return attributes;
};

export const getFullProducts = (storageProducts) => {
  return storageProducts.filter((product) =>
    product.storageItems.every((item) => item.quantity === item.initialQuantity)
  );
};

export const getStorageProductsGroupById = (storageProducts) => {
  const productsGroupById = {};

  for (let i = 0; i < storageProducts.length; i++) {
    const { image, productId, productName, shortDesc, isWineBox } = storageProducts[i];
    if (productsGroupById[productId]) {
      productsGroupById[productId].quantity += 1;
      productsGroupById[productId].storageProducts.push(storageProducts[i]);
    } else {
      productsGroupById[productId] = {
        image,
        isWineBox,
        quantity: 1,
        productName,
        shortDesc,
        storageProducts: [storageProducts[i]]
      };
    }
  }

  return productsGroupById;
};

export const getStorageProductsGroupByIdForCart = (cartStorage) => {
  const productsGroupById = [];

  for (let i = 0; i < cartStorage.length; i++) {
    const { isWineBox } = cartStorage[i];
    const { image, productId, productName, shortDesc } = cartStorage[i].storageProductForCart;
    if (productsGroupById[productId]) {
      productsGroupById[productId].quantity += 1;
      productsGroupById[productId].storageProducts.push(cartStorage[i]);
    } else {
      productsGroupById[productId] = {
        image,
        isWineBox,
        quantity: 1,
        productName,
        shortDesc,
        storageProducts: [cartStorage[i]]
      };
    }
  }

  return Object.values(productsGroupById);
};

export const isWineBoxAvailable = (storageProducts) => {
  return storageProducts.some((product) => product.wineBoxAvailable);
};

export const setCartStorageItems = (storageProductGroupById, qty, isDelete = false) => {
  const quantityToAdd = Math.min(
    isDelete ? storageProductGroupById.storageProducts.length - qty.value : qty.value,
    storageProductGroupById.storageProducts.length
  );
  const cartStorageItems = [];

  for (let i = 0; i < quantityToAdd; i++) {
    const storageItems = storageProductGroupById.storageProducts[i].storageItems?.map((
      { itemId, shortDesc, quantity, equivbtl, attributes, itemNameDisplay }
    ) => ({
      itemId,
      shortDesc,
      quantity,
      equivbtl,
      attributes,
      itemName: itemNameDisplay
    }));

    let storageProductForCart = {};
    if (storageItems) {
      const {
        barCode,
        image,
        productId,
        productNameDisplay,
        purchasePrice,
        shortDesc
      } = storageProductGroupById.storageProducts[i];

      storageProductForCart = {
        attributes: storageItems[0].attributes,
        barCode,
        image,
        productId,
        productName: productNameDisplay,
        purchasePrice,
        shortDesc,
        storageItemsForCart: storageItems
      };
    } else {
      storageProductForCart = storageProductGroupById.storageProducts[i].storageProductForCart;
    }

    const isWineBox = storageProductGroupById.storageProducts[i]?.isWineBox;

    const cartStorageItem = {
      isWineBox: isWineBox || false,
      quantity: Number(!isDelete),
      storageProductForCart,
      storageProductsForCart: storageProductGroupById.storageProducts[i].storageProductsForCart || null
    };

    cartStorageItems.push(cartStorageItem);
  }

  return cartStorageItems;
};

export const groupWineBoxItemsById = (wineBoxItems) => {
  const itemsGroupById = {};
  wineBoxItems.map((item) => {
    const itemId = item.itemId;
    if (itemsGroupById[itemId]) {
      itemsGroupById[itemId].quantity += 1;
    } else {
      itemsGroupById[itemId] = {
        ...item
      };
    }
  });

  return Object.values(itemsGroupById);
};

export const getStorageWineboxContent = (cartStorageItem) => {
  if (cartStorageItem.storageProductForCart.barCode) {
    return cartStorageItem.storageProductForCart.storageItemsForCart.map((p) =>
      `${p.quantity} x ${p.itemName} - ${getAttributeValue(p, 'couleur')}`
    ).join('<br />');
  } else {
    return cartStorageItem.storageProductsForCart.map((sp) => (
      sp.storageItemsForCart.map((i) =>
        `${i.quantity} x ${i.itemName} - ${getAttributeValue(i, 'couleur')}`
      ).join('<br />')
    )).join('<br />');
  }
};

export const duplicateItem = (itemsWB) => {
  const duplicatedArray = itemsWB.flatMap((item) => {
    if (item.quantity > 1) {
      return Array.from({ length: item.quantity }, () => {
        const duplicatedItem = structuredClone(item);
        duplicatedItem.quantity = 1;
        return duplicatedItem;
      });
    } else {
      return [item];
    }
  });

  return duplicatedArray;
};

export const formatItemForHistory = (storageHistory) => {
  const { image, isWineBox, productName, attributes } = storageHistory.storageProduct;
  const { quantity } = storageHistory.storageProduct.storageItems[0];
  const item = {
    attributes,
    image,
    isWineBox,
    itemName: productName,
    quantity
  };

  return item;
};

export const getQtyAndFormatForHistory = (t, storageHistory) => {
  const { isWineBox, shortDesc } = storageHistory.storageProduct;
  let quantity = 0;
  if (storageHistory.storageProduct.isWineBox) {
    storageHistory.storageProduct.storageItems.map((storageItem) => {
      quantity += storageItem.quantity;
    });
  } else {
    quantity = storageHistory.storageProduct.storageItems[0].quantity;
  }

  return shortDesc ? `${quantity} ${shortDesc}` :
    (isWineBox && !shortDesc) ? t('attributes.formatMixed') : null;
};

export const getIconColor = (color) => {
  return [
    !color && 'mixed',
    color?.valueId === '8' && COLOR_LABEL.RED,
    color?.valueId === '5' && COLOR_LABEL.WHITE,
    color?.valueId === '2' && COLOR_LABEL.PINK
  ].filter(Boolean).join(' ');
};

export const computeRegionChartData = ({ totalBtl, stats }) => {
  let cumulatePercentage = 0;
  return stats?.region?.map((reg, i) => {
    cumulatePercentage += reg.number;
    return {
      name: reg.name,
      color: CHART_COLORS[stats.region.length][i],
      percentage: Math.round(((cumulatePercentage / totalBtl) * 100) * 100) / 100,
      details: reg.details
    };
  });
};

export const computeStatCardTitle = (t, attributeName, { stats }) => (
  t(`dashboard.stats.${attributeName}`, { count: stats?.[attributeName]?.length })
);

export const computeDataName = (t, data) => {
  const getNameTranslation = {
    other: t('dashboard.other.otherHiddenInfos'),
    default: data
  };

  return getNameTranslation[data] || getNameTranslation.default;
};

export const computeOtherDataNamesForTooltip = (data) => {
  return data?.map(({ name }) =>
    <div className="content" key={name}>
      {name}
    </div>
  );
};
